import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { AxiosResponse } from "axios";
import type { ApiResponse, ApiError } from "~/types";
import { logError, logWarning } from "./logger";
import { toast } from "~/lib/toast";
import { SERVER_FAULT_MESSAGE, plainServerFaultMessage } from "~/lib/server-fault-message";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * When a service uses `responseType: "blob"` (CSV / PDF / file downloads),
 * axios delivers a 4xx error body as a Blob rather than parsed JSON. The
 * structured error fields the backend sent (`code`, `roleName`, `message`,
 * etc.) are unreachable inside the Blob, so `extractErrorMessage` falls
 * through to the generic default and any backend-specific UI handling
 * (e.g. an "Unmapped roles" modal) never fires.
 *
 * Call this before `handleApiError` in any catch block for a blob-mode
 * request. It mutates `error.response.data` from `Blob` → parsed JSON
 * in-place, so downstream consumers can read structured error codes.
 *
 * Safe to call regardless of `responseType` — it's a no-op when
 * `error.response.data` is not a Blob.
 *
 * @example
 *   try {
 *     const r = await axiosPrivate.get(url, { responseType: "blob" });
 *     return r.data as Blob;
 *   } catch (error) {
 *     await unwrapBlobError(error);
 *     handleApiError(error, "Failed to export…", { url, method: "GET" });
 *   }
 */
export async function unwrapBlobError(error: unknown): Promise<void> {
  const axiosErr = error as { response?: { data?: unknown } } | null;
  if (axiosErr?.response?.data instanceof Blob) {
    try {
      const text = await (axiosErr.response.data as Blob).text();
      axiosErr.response.data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      // Not JSON — leave the Blob in place. extractErrorMessage will fall
      // through to the default message and the call still surfaces an error.
    }
  }
}

/**
 * Strips raw internal backend error text (Prisma exceptions, stack traces,
 * internal class names) from an API response message so that restaurant
 * managers never see raw ORM or database errors.
 */
function sanitizeInternalErrorMessage(message: string): string {
  const internalPatterns = [
    "PrismaClientKnownRequestError",
    "PrismaClientUnknownRequestError",
    "PrismaClientValidationError",
    "PrismaClientRustPanicError",
    "PrismaClientInitializationError",
    "Unique constraint failed",
    "Foreign key constraint failed",
    "An operation failed because it depends on",
    "Error occurred in the query engine",
    "Invalid `prisma.",
  ];
  const isInternal = internalPatterns.some((p) => message.includes(p));
  if (isInternal) {
    return SERVER_FAULT_MESSAGE;
  }
  return message;
}

/**
 * The backend puts a machine-readable code in `error` and the human sentence in
 * `message` — `{ error: "EOD_SETTLED", message: "Cannot void this order…" }`.
 * `extractErrorMessage` only ever looked for `code` / `details.code`, so every
 * one of those codes arrived as `errorCode: undefined` and no caller could
 * branch on it; the finance modals were reduced to matching HTTP status alone.
 *
 * Nest also puts human text in the same field for its own exceptions
 * ("Bad Request", "Forbidden"), so only SCREAMING_SNAKE_CASE counts as a code.
 */
function machineCode(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Z][A-Z0-9_]*$/.test(value)
    ? value
    : undefined;
}

/**
 * Extracts error message from axios error response
 * Priority: message > error > detail > non_field_errors > Error.message > default
 *
 * @param error - The error object (usually from axios catch block)
 * @param defaultMessage - Default message if no error message is found
 * @returns Object containing error message, statusCode, and errorCode
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage: string = "An error occurred",
): {
  message: string;
  statusCode?: number;
  errorCode?: string;
  errorData?: ApiError;
} {
  const axiosError = error as {
    response?: { data?: ApiError; status?: number };
    request?: any;
    code?: string;
    message?: string;
  };
  const errorData = axiosError?.response?.data;

  // 1. If we have a response from the server, use that
  if (errorData) {
    const rawMessage =
      errorData.message ||
      errorData.error ||
      errorData.detail ||
      errorData.non_field_errors?.[0];

    if (rawMessage) {
      const rawStr = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : String(rawMessage);
      // Sanitize raw internal backend error text (Prisma exceptions, etc.)
      // to prevent leaking ORM/stack information to restaurant managers.
      const sanitized = sanitizeInternalErrorMessage(rawStr);
      const status = errorData.statusCode || axiosError.response?.status;
      return {
        // A 5xx's stock text ("Internal server error") becomes plain words
        // here, so every hook that toasts `error.message` says it too.
        message: plainServerFaultMessage(sanitized, status),
        statusCode: status,
        errorCode:
          errorData.details?.code ||
          errorData.code ||
          machineCode(errorData.error),
        errorData,
      };
    }
  }

  // 2. If it's a network error (request sent but no response)
  if (!axiosError?.response && axiosError?.request) {
    let networkMsg = "Unable to connect to the server.";

    if (
      axiosError.code === "ECONNABORTED" ||
      axiosError.message?.includes("timeout")
    ) {
      networkMsg =
        "Request timed out. Please check your internet connection and try again.";
    } else if (
      typeof window !== "undefined" &&
      window.navigator &&
      window.navigator.onLine === false
    ) {
      networkMsg =
        "No internet connection. Please check your network and try again.";
    }

    return {
      message: networkMsg,
      statusCode: 0,
      errorCode: axiosError.code || "ERR_NETWORK",
      errorData: undefined,
    };
  }

  // 3. Fallback to generic Error message or default. An Error thrown by
  // `handleApiError` has already consumed the response and carries its status,
  // code and body as properties — read them back, or every caller that branches
  // on status after a service call sees `undefined` (preflight admin/X1:
  // the change-plan block step never rendered).
  const rawMessage = error instanceof Error ? error.message : defaultMessage;
  const friendly = error as
    | { statusCode?: unknown; errorCode?: unknown; errorData?: ApiError }
    | undefined;
  const statusCode =
    axiosError?.response?.status ??
    (typeof friendly?.statusCode === "number" ? friendly.statusCode : undefined);

  return {
    // A gateway page has no JSON body, so axios's own "Request failed with
    // status code 502" lands here.
    message: plainServerFaultMessage(rawMessage, statusCode),
    statusCode,
    errorCode:
      (typeof friendly?.errorCode === "string" ? friendly.errorCode : undefined) ??
      (error as any)?.code,
    errorData: friendly?.errorData,
  };
}


/**
 * Codes the API returns when an org simply does not HOLD a capability.
 *
 * These are correct authorisation outcomes, not application errors. A
 * People-Door venue hitting `/marketing/campaigns` gets a 403 because the plan
 * does not include marketing — the system working, not failing.
 *
 * Measured 2026-09-21 (WQ-1.25): `/marketing` logged **10 errors** on a single
 * visit, five of them these 403s, every one at `level: "error"` and every one
 * reaching Sentry. That is the noise a genuine defect has to be spotted among,
 * and it is the reason this list exists: the call still THROWS so the caller
 * can render a not-on-your-plan state, it is just no longer reported as a
 * fault.
 *
 * Deliberately a CODE list, not `statusCode === 403`. A 403 on an endpoint the
 * user should have reached IS a defect and must keep its error level — the
 * only ones downgraded here are the ones the backend has explicitly named.
 */
const CAPABILITY_DENIAL_CODES = new Set([
  'FEATURE_NOT_AVAILABLE',
  'INVENTORY_NOT_ENABLED',
  // SIM-W43 — a location-restricted user opening Payouts: payouts belong to the
  // business, not a location. Arrives as `details.code`, which the extractor reads
  // first. The Payouts page renders an explanation for it; it is not a fault.
  'PAYOUTS_BUSINESS_LEVEL',
]);

/**
 * The `error` discriminator, checked SEPARATELY from `code`, because the two
 * exceptions do not answer the same way.
 *
 * `FeatureNotAvailableException` sends `code: 'FEATURE_NOT_AVAILABLE'`.
 * `SubscriptionLockedException` sends no `code` at all — only
 * `error: 'SubscriptionLocked'` — and `machineCode()` rejects that because its
 * `^[A-Z][A-Z0-9_]*$` shape does not match CamelCase. Matching on `code` alone
 * would therefore have silently missed every subscription lock, which is the
 * half of the noise a brand-new org sees on every surface.
 */
const CAPABILITY_DENIAL_ERRORS = new Set([
  'SubscriptionLocked',
  'FeatureNotAvailable',
]);

/** Is this failure an org lacking a capability, rather than something broken? */
export function isCapabilityDenial(
  statusCode: number | undefined,
  errorCode: string | undefined,
  errorData?: { error?: unknown } | null,
): boolean {
  if (statusCode !== 403) return false;
  if (errorCode && CAPABILITY_DENIAL_CODES.has(errorCode)) return true;
  const discriminator = errorData?.error;
  return (
    typeof discriminator === 'string' &&
    CAPABILITY_DENIAL_ERRORS.has(discriminator)
  );
}

/**
 * Global API error handler that standardizes logging and error wrapping
 *
 * @param error - The error object from catch block
 * @param defaultMessage - User-facing message for the error
 * @param context - Context for logging (url, method, type, etc)
 * @returns never (throws friendly error)
 */
export function handleApiError(
  error: unknown,
  defaultMessage: string,
  context: {
    url: string;
    method: string;
    errorType: string;
    extraData?: Record<string, unknown>;
  },
): never {
  const {
    message: errorMessage,
    statusCode,
    errorCode,
    errorData,
  } = extractErrorMessage(error, defaultMessage);

  const friendlyError = new Error(errorMessage) as Error & {
    originalError?: unknown;
    statusCode?: number;
    errorCode?: string;
    errorData?: ApiError;
  };
  friendlyError.originalError = error;
  friendlyError.statusCode = statusCode;
  friendlyError.errorCode = errorCode;
  friendlyError.errorData = errorData;

  const rawAxios =
    error && typeof error === "object" && "isAxiosError" in error
      ? {
          rawAxiosMessage:
            "message" in error
              ? String((error as { message?: unknown }).message)
              : undefined,
          rawAxiosCode: "code" in error ? String((error as { code?: string }).code) : undefined,
        }
      : error instanceof Error
        ? {
            rawAxiosMessage: error.message,
            rawAxiosCode: (error as { code?: string }).code,
          }
        : {};

  const logPayload = {
    category: "api_error",
    data: {
      url: context.url,
      method: context.method,
      statusCode,
      errorCode,
      errorMessage,
      ...rawAxios,
      ...context.extraData,
    },
    tags: {
      error_type: context.errorType,
      status_code: String(statusCode || "unknown"),
    },
  };

  // A capability the org does not hold is the system working. Still logged —
  // silence would make a plan misconfiguration invisible — but at warning
  // level, so it stops competing with real faults in Sentry. The throw below
  // is unchanged: callers still get to render a not-on-your-plan state.
  if (isCapabilityDenial(statusCode, errorCode, errorData)) {
    logWarning(defaultMessage, logPayload);
  } else {
    logError(defaultMessage, friendlyError, logPayload);
  }

  throw friendlyError;
}

/**
 * Formats a number as currency (USD)
 * @param value - The number to format
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | string | undefined | null,
): string {
  if (value === undefined || value === null) return "$0.00";
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numericValue)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numericValue);
}

/**
 * Formats a list of prices (sizes/variants) with truncation (+X more)
 * @param items - Array of variants or sizes
 * @param maxVisible - Maximum number of prices to show before truncating
 * @returns Formatted string
 */
export function formatPriceList(
  items: any[] | undefined,
  maxVisible = 3,
): string {
  if (!items || items.length === 0) return "";

  const sortedItems = [...items].sort(
    (a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0),
  );

  const displayedItems = sortedItems.slice(0, maxVisible);
  const remainingCount = sortedItems.length - maxVisible;

  let result = displayedItems
    .map(
      (v: any) =>
        `${v.name.charAt(0).toUpperCase()} $${Number(v.price).toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        )}`,
    )
    .join(" ");

  if (remainingCount > 0) {
    result += ` +${remainingCount} more`;
  }

  return result;
}

/**
 * Normalizes API response to cleanly pull data whether the backend wraps it alongside meta objects or not.
 * @param response AxiosResponse containing our ApiResponse wrapper
 * @returns Inner data T or null
 */
export function extractData<T>(
  response: AxiosResponse<ApiResponse<T>>,
): T | null {
  const rsData = response.data?.data;
  if (rsData === undefined || rsData === null) return null;

  // Some backend controllers redundantly wrap their return value in { data: T }
  // on top of the ResponseInterceptor's own { data: ... } wrapper. This creates
  // a double-wrapped shape: { data: { data: T } }. Unwrap ONLY when the object's
  // sole key is "data" — if other keys exist (e.g. "meta" in paginated responses,
  // or domain fields like "granularity"), it's a real payload, not a wrapper.
  if (
    typeof rsData === "object" &&
    !Array.isArray(rsData) &&
    "data" in rsData
  ) {
    const keys = Object.keys(rsData);
    if (keys.length === 1 && keys[0] === "data") {
      return (rsData as { data: T }).data as T;
    }
  }

  return rsData as T;
}

/**
 * Extracts a list/array from an API response, tolerating these shapes:
 *   - [ ... ]                                (plain array)
 *   - { data: [ ... ] }                      (double-wrapped)
 *   - { data: [ ... ], meta: { ... } }       (paginated list)
 * Always returns an array. Use for list endpoints that caller expects as T[].
 */
export function extractList<T>(response: AxiosResponse<ApiResponse<T[]>>): T[] {
  const extracted = extractData<T[]>(response);
  if (Array.isArray(extracted)) return extracted;

  if (
    extracted &&
    typeof extracted === "object" &&
    "data" in (extracted as Record<string, unknown>) &&
    Array.isArray((extracted as { data: unknown }).data)
  ) {
    return (extracted as { data: T[] }).data;
  }

  // Fall back to the raw payload in case the response shape skipped the wrapper.
  const raw = response.data as unknown;
  if (Array.isArray(raw)) return raw as T[];

  return [];
}



